import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";

/**
 * Non-dismissible modal rendered by ProtectedRoute when mustChangePassword=true.
 * The only exit is a successful password change or sign-out.
 */
export function ForceChangePasswordDialog() {
  const { changePassword, signOut } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const valid = next.length >= 8 && next === confirm;

  async function submit() {
    setError(null);
    if (!valid) {
      setError("New password must be at least 8 characters and match confirmation.");
      return;
    }
    setBusy(true);
    try {
      await changePassword(current, next);
      // ProtectedRoute will unmount this dialog on success.
    } catch (e) {
      const err = e as Error & { body?: { error?: string; message?: string } };
      // Prefer the server's human-readable message (e.g. "Current password is
      // incorrect.") over the machine error code — added when change-password
      // gained server-side currentPassword verification (2026-09-24).
      setError(err.body?.message ?? err.body?.error ?? "Failed to change password.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open>
      <DialogContent
        className="sm:max-w-md [&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Set a new password</DialogTitle>
          <DialogDescription>
            Your administrator set a temporary password. Please set a new one to continue.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="space-y-3"
        >
          <div>
            <Label htmlFor="fcp-current">Current password</Label>
            <Input
              id="fcp-current"
              type={show ? "text" : "password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="fcp-new">New password</Label>
            <Input
              id="fcp-new"
              type={show ? "text" : "password"}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          <div>
            <Label htmlFor="fcp-confirm">Confirm new password</Label>
            <Input
              id="fcp-confirm"
              type={show ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-ink-600">
              <input
                type="checkbox"
                checked={show}
                onChange={(e) => setShow(e.target.checked)}
              />
              Show
            </label>
            <button
              type="button"
              className="text-sm text-ink-500 underline"
              onClick={() => signOut()}
            >
              Sign out
            </button>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <Button type="submit" disabled={!valid || busy} className="w-full">
            {busy ? "Saving…" : "Update password"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
